import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Row, Col, Table } from 'react-bootstrap';
import { authFetch } from '../utils/authFetch';

const empty = () => ({ problema: '', lucrari: '', piese: '', valoare_anterioara: '',
  valori_fara_tva: true, piese_lei: '0', manopera_lei: '0', tva_lei: '0', cota_tva: '', termen: '', data_constatare: new Date().toLocaleDateString('en-CA') });
const calculateAmounts = draft => ({ ...draft,
  tva_lei: (Math.round((Number(draft.piese_lei) + Number(draft.manopera_lei)) * Number(draft.cota_tva) + 1e-8) / 100).toFixed(2)
});
const labels = { asteptare: 'În așteptarea acordului', acceptat: 'Acceptată', refuzat: 'Refuzată', anulat: 'Anulată de service' };
export const printAnnex = (orderId, annexId) => window.open(`/comenzi/interna/${orderId}/anexe/${annexId}/print`, '_blank', 'noopener');

export default function ServiceAnnexes({ orderId }) {
  const [items, setItems] = useState([]);
  const [vatOptions, setVatOptions] = useState([]);
  const [vatReady, setVatReady] = useState(false);
  const [vatExempt, setVatExempt] = useState(false);
  const [draft, setDraft] = useState(empty);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [decision, setDecision] = useState(null);
  const [cancellation, setCancellation] = useState(null);
  const endpoint = `/comenzi/interna/${orderId}/anexe`;
  useEffect(() => {
    let active = true;
    if (!orderId) return;
    authFetch(endpoint).then(rows => { if (active) { setItems(rows); setLoaded(true); } })
      .catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [orderId, endpoint]);
  useEffect(() => {
    let active = true;
    setVatReady(false);
    Promise.all([authFetch('/bxvat?limit=100'), authFetch('/firma')]).then(([vats, firms]) => {
      if (!active) return;
      const rates = [...new Set((vats.items || vats || []).filter(v => !v.is_reverse_charge)
        .map(v => Number(v.procent)).filter(v => Number.isFinite(v) && v >= 0 && v <= 100))].sort((a,b) => b-a);
      if (!rates.length) throw new Error('Nu sunt configurate cote TVA pentru anexă.');
      const firm = (firms.items || firms || [])[0];
      const exempt = firm?.platitor_tva != null && Number(firm.platitor_tva) === 0;
      const rate = exempt ? 0 : rates[0];
      setVatExempt(exempt); setVatOptions(exempt ? [0] : rates);
      setDraft(prev => calculateAmounts({ ...prev, cota_tva: String(rate) }));
      setVatReady(true);
    }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [orderId]);
  if (!orderId) return <Alert variant="info">Salvează comanda înainte de a adăuga anexe.</Alert>;
  const change = (key, val) => setDraft(prev => calculateAmounts({ ...prev, [key]: val }));
  const create = async e => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const row = await authFetch(endpoint, { method: 'POST', body: JSON.stringify(draft) });
      setItems(prev => [...prev, row]); setDraft(prev => ({ ...empty(), cota_tva: prev.cota_tva }));
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const decide = async e => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const { id, ...data } = decision;
      const row = await authFetch(`${endpoint}/${id}/decizie`, { method: 'POST', body: JSON.stringify(data) });
      setItems(prev => prev.map(r => r.id === row.id ? row : r)); setDecision(null);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const cancel = async e => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const row = await authFetch(`${endpoint}/${cancellation.id}/anulare`, { method: 'POST', body: JSON.stringify({ motiv: cancellation.motiv }) });
      setItems(prev => prev.map(r => r.id === row.id ? row : r)); setCancellation(null);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return <div className="mt-3">
    <Alert variant="info">Lucrările suplimentare se execută numai după acordul scris al clientului. Anexele salvate se păstrează nemodificate; pentru alte constatări se adaugă o anexă nouă.</Alert>
    {error && <Alert variant="danger">{error}</Alert>}
    <Table bordered size="sm"><thead><tr><th>Anexă</th><th>Problemă nouă</th><th>Supliment (lei)</th><th>Decizie</th><th>Acțiuni</th></tr></thead>
      <tbody>{items.map(row => <tr key={row.id}>
        <td>{row.number}</td><td style={{ whiteSpace: 'pre-wrap' }}>{row.data.problema}</td>
        <td>{row.data.supliment_total} lei {row.data.valori_fara_tva ? '(fără TVA)' : '(cu TVA)'}</td><td>{labels[row.status]}</td>
        <td><Button size="sm" onClick={() => printAnnex(orderId, row.id)}>Print anexă</Button>{' '}
          {row.status === 'asteptare' && <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => setDecision({ id: row.id, decizie: 'acceptat', semnatar: '', data_semnarii: '', document_semnat: '', modalitate: 'semnatura', mesaj: '', confirm_acord_scris: false })}>Înregistrează acord / refuz scris</Button>}
          {' '}{row.status === 'asteptare' && <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => { setDecision(null); setCancellation({ id: row.id, motiv: '' }); }}>Anulează propunerea</Button>}
        </td></tr>)}</tbody></Table>
    {decision && <Form onSubmit={decide} className="border rounded p-3 mb-4">
      <h6>Decizia clientului pentru anexa selectată</h6>
      <Form.Select aria-label="Decizie client" value={decision.decizie} onChange={e => setDecision({ ...decision, decizie: e.target.value })}>
        <option value="acceptat">Acceptă integral</option><option value="refuzat">Refuză</option></Form.Select>
      <p className="small text-muted">Pentru accept parțial, consemnează refuzul acestei propuneri și emite o anexă nouă numai cu lucrările acceptate.</p>
      <Form.Group controlId="annex-modalitate"><Form.Label>Forma răspunsului scris</Form.Label>
        <Form.Select value={decision.modalitate} onChange={e => setDecision({ ...decision, modalitate: e.target.value })}>
          <option value="semnatura">Document semnat</option><option value="sms">SMS</option><option value="email">E-mail</option>
        </Form.Select></Form.Group>
      {[["semnatar", "Numele clientului / reprezentantului", "text"], ["data_semnarii", "Data și ora răspunsului scris", "datetime-local"],
        ["document_semnat", "Referința documentului / mesajului și locul arhivării", "text"]].map(([key, label, type]) =>
        <Form.Group className="mb-2" controlId={`annex-${key}`} key={key}><Form.Label>{label}</Form.Label>
          <Form.Control required type={type} value={decision[key]} onChange={e => setDecision({ ...decision, [key]: e.target.value })} /></Form.Group>)}
      {decision.modalitate !== 'semnatura' && <Form.Group controlId="annex-mesaj"><Form.Label>Mesajul clientului (costuri și termen acceptate / refuzate)</Form.Label>
        <Form.Control required as="textarea" rows={3} value={decision.mesaj} onChange={e => setDecision({ ...decision, mesaj: e.target.value })} /></Form.Group>}
      <Form.Check required id="annex-signed" label="Confirm că am primit și arhivat răspunsul scris al clientului pentru această anexă, costurile și termenul ei."
        checked={decision.confirm_acord_scris} onChange={e => setDecision({ ...decision, confirm_acord_scris: e.target.checked })} />
      <Button className="mt-2" type="submit" disabled={busy}>Salvează decizia</Button>{' '}
      <Button className="mt-2" variant="secondary" onClick={() => setDecision(null)}>Renunță</Button>
    </Form>}
    {cancellation && <Form onSubmit={cancel} className="border rounded p-3 mb-4">
      <Form.Group controlId="annex-cancel-reason"><Form.Label>Motivul anulării propunerii de către service</Form.Label>
        <Form.Control required as="textarea" rows={2} value={cancellation.motiv} onChange={e => setCancellation({ ...cancellation, motiv: e.target.value })} /></Form.Group>
      <p className="small">Anexa rămâne în istoric. Anularea nu reprezintă acord sau refuz al clientului.</p>
      <Button variant="danger" type="submit" disabled={busy}>Consemnează anularea</Button>{' '}
      <Button variant="secondary" onClick={() => setCancellation(null)}>Renunță</Button>
    </Form>}
    <Form onSubmit={create}>
      <h6>Anexă nouă — constatare suplimentară</h6>
      <Row className="g-3">
        {[["problema", "Problema nouă identificată"], ["lucrari", "Lucrări suplimentare propuse"], ["piese", "Piese și materiale necesare"]].map(([key, label]) =>
          <Col md={key === 'piese' ? 12 : 6} key={key}><Form.Group controlId={`annex-${key}`}><Form.Label>{label}</Form.Label>
            <Form.Control as="textarea" rows={4} required={key !== 'piese'} value={draft[key]} onChange={e => change(key, e.target.value)} /></Form.Group></Col>)}
        {[["valoare_anterioara", "Total deja aprobat în comandă, fără TVA (lei)"], ["piese_lei", "Piese suplimentare fără TVA (lei)"], ["manopera_lei", "Manoperă suplimentară fără TVA (lei)"], ["tva_lei", "TVA supliment (lei)"]].map(([key, label]) =>
          <Col md={3} key={key}><Form.Group controlId={`annex-${key}`}><Form.Label>{label}</Form.Label><Form.Control required readOnly={key === "tva_lei"} type="number" min="0" step="0.01" value={draft[key]} onChange={e => change(key, e.target.value)} /></Form.Group></Col>)}
        <Col md={3}><Form.Group controlId="annex-vat-rate"><Form.Label>Cotă TVA supliment (%)</Form.Label>
          <Form.Select required disabled={!vatReady || vatExempt} value={draft.cota_tva} onChange={e => change('cota_tva', e.target.value)}>
            {!vatReady && <option value="">Se încarcă…</option>}
            {vatOptions.map(rate => <option key={rate} value={String(rate)}>{rate}%</option>)}
          </Form.Select></Form.Group></Col>
        <Col md={6}><Form.Group controlId="annex-date"><Form.Label>Data constatării</Form.Label><Form.Control required type="date" value={draft.data_constatare} onChange={e => change('data_constatare', e.target.value)} /></Form.Group></Col>
        <Col md={6}><Form.Group controlId="annex-term"><Form.Label>Noul termen estimat</Form.Label><Form.Control required value={draft.termen} onChange={e => change('termen', e.target.value)} /></Form.Group></Col>
      </Row>
      <Row className="g-3 my-3">
        <Col md={6}><Form.Group controlId="annex-total-added">
          <Form.Label>Total adăugat în anexă, fără TVA (lei)</Form.Label>
          <Form.Control readOnly value={(Number(draft.piese_lei) + Number(draft.manopera_lei)).toFixed(2)} />
        </Form.Group></Col>
        <Col md={6}><Form.Group controlId="annex-total-new">
          <Form.Label>Total inclusiv anexă, fără TVA (lei)</Form.Label>
          <Form.Control readOnly value={(Number(draft.valoare_anterioara) + Number(draft.piese_lei) + Number(draft.manopera_lei)).toFixed(2)} />
        </Form.Group></Col>
      </Row>
      <Button type="submit" disabled={busy || !loaded || !vatReady}>Salvează anexa pentru semnare</Button>
    </Form>
  </div>;
}
